/*
 * Copyright (c) 2020 Little Robot Friends, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {Block} from 'baseui/block'
import {Navigation} from 'baseui/side-navigation'
import React from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {navigation} from '../../utils/config'

export const NavItemTitle = ({icon, title, disabled}) => {
  return (
    <Block
      overrides={{
        Block: {
          style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            opacity: disabled ? 0.5 : 1,
          },
        },
      }}
    >
      <Block
        overrides={{
          Block: {
            style: ({$theme}) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: $theme.sizing.scale800,
              ...$theme.typography.HeadingXSmall,
              marginBottom: $theme.sizing.scale600,
              marginLeft: $theme.sizing.scale400,
              marginRight: $theme.sizing.scale400,
              marginTop: $theme.sizing.scale600,
              [$theme.media.medium]: {
                marginLeft: $theme.sizing.scale600,
                marginRight: $theme.sizing.scale600,
                ...$theme.typography.HeadingLarge,
              },
              [$theme.media.large]: {
                marginBottom: 0,
                marginLeft: 0,
                marginRight: $theme.sizing.scale200,
                marginTop: 0,
                ...$theme.typography.LabelMedium,
              },
            }),
          },
        }}
      >
        <FontAwesomeIcon icon={icon} />
      </Block>
      <Block
        $as="span"
        overrides={{
          Block: {
            style: ({$theme}) => ({
              display: 'none',
              [$theme.media.large]: {
                display: 'block',
              },
            }),
          },
        }}
      >
        {title}
      </Block>
    </Block>
  )
}

const Sidebar = () => {
  const history = useHistory()
  const location = useLocation()

  return (
    <Navigation
      items={navigation.xl.sidebar}
      activeItemId={location.pathname}
      onChange={async ({event, item}) => {
        event.preventDefault()
        await history.push(item.itemId)
      }}
      overrides={{
        NavLink: {
          style: ({href}) => {
            return {
              pointerEvents: typeof href === 'string' && href.includes('#') ? 'none' : 'auto',
              cursor: typeof href === 'string' && href.includes('#') ? 'default' : 'pointer',
            }
          },
        },
        NavItem: {
          style: ({$active, $selectable, $theme}) => {
            if (!$selectable) {
              return {
                color: $theme.colors.primary500,
                fontWeight: '900',
                textTransform: 'uppercase',
                display: 'none',
                [$theme.media.large]: {
                  display: 'block',
                },
              }
            }
            if (!$active)
              return {
                borderBottomRightRadius: $theme.borders.radius300,
                borderTopRightRadius: $theme.borders.radius300,
                color: $theme.colors.primary700,
                fontWeight: 700,
                paddingLeft: $theme.sizing.scale500,
                ':hover': {
                  backgroundColor: $theme.colors.primary100,
                  borderLeftColor: $theme.colors.primary700,
                  color: $theme.colors.primary600,
                },
                [$theme.media.large]: {
                  paddingLeft: $theme.sizing.scale800,
                },
              }

            return {
              backgroundColor: $theme.colors.primary50,
              borderBottomRightRadius: $theme.borders.radius300,
              borderLeftColor: $theme.colors.primary700,
              borderTopRightRadius: $theme.borders.radius300,
              color: $theme.colors.primary600,
              cursor: 'default',
              fontWeight: 700,
              paddingLeft: $theme.sizing.scale500,
              ':hover': {
                color: $theme.colors.primary500,
              },
              [$theme.media.large]: {
                paddingLeft: $theme.sizing.scale800,
              },
            }
          },
        },
      }}
    />
  )
}

export default Sidebar
